import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { SimulationProvider } from '../../contexts/SimulationContext';
import RunControl from './RunControl';
import LiveRun from './LiveRun';
import BatchStats from './BatchStats';
import ResultWindow from './ResultWindow';
import RunOfficerSimulator from './RunOfficerSimulator';
import Profile from '../AdminDashboard/Profile';
import LoadingSpinner from '../common/LoadingSpinner';

import CONFIG from '../../config';
import './RunOfficerDashboard.css';


const RunOfficerDashboard = ({ user, onLogout }) => {
    const [activeTab, setActiveTab] = useState('run-control');
    const [assignedProjects, setAssignedProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        fetchAssignedProjects();
    }, []);

    const fetchAssignedProjects = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/projects/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssignedProjects(resp.data);
            if (resp.data.length > 0) {
                setSelectedProject(resp.data[0]);
            }
        } catch (err) {
            console.error('Failed to fetch assigned projects:', err);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <LoadingSpinner message="Fetching Assigned Missions..." />
                </div>
            );
        }
        if (!selectedProject) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                    <Icon icon="ion:alert-circle-outline" width="64" />
                    <h2 style={{ marginTop: '20px' }}>No Projects Assigned</h2>
                    <p>You are not currently assigned to any active recruitment drives as a Run Officer.</p>
                </div>
            );
        }

        switch (activeTab) {
            case 'run-control':
                return <RunControl project={selectedProject} onStartRun={() => setActiveTab('live-run')} />;
            case 'live-run':
                return <LiveRun project={selectedProject} onBack={() => setActiveTab('run-control')} />;
            case 'analytics':
                return <BatchStats project={selectedProject} />;
            case 'results':
                return <ResultWindow project={selectedProject} />;
            case 'profile':
                return <Profile user={user} />;

            default:
                return <RunControl project={selectedProject} onStartRun={() => setActiveTab('live-run')} />;
        }

    };

    const userObj = JSON.parse(localStorage.getItem('user') || '{}');

    // Modified Sidebar for Run Officer
    const menuGroups = [
        {
            title: 'RACE OPERATIONS',
            items: [
                { id: 'run-control', icon: 'ion:speedometer-outline', label: 'Run Control' },
                { id: 'live-run', icon: 'ion:play-circle-outline', label: 'Live Race' },
            ]
        },
        {
            title: 'DATA & ANALYTICS',
            items: [
                { id: 'analytics', icon: 'ion:stats-chart-outline', label: 'Batch Stats' },
                { id: 'results', icon: 'ion:ribbon-outline', label: 'Result Window' },
            ]
        },
        {
            title: 'USER SETTINGS',
            items: [
                { id: 'profile', icon: 'ion:person-outline', label: 'My Profile' },
            ]
        }
    ];

    return (
        <SimulationProvider>
            <div className="ro-dashboard-container">
                {/* Custom Sidebar Matching Master Admin 100% */}
                <aside className={`ro-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="ro-sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
                        <Icon icon={isCollapsed ? "ion:chevron-forward" : "ion:chevron-back"} />
                    </div>

                    <div style={{
                        padding: isCollapsed ? '24px 0' : '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isCollapsed ? '0' : '12px',
                        borderBottom: '1px solid var(--border-light)',
                        minHeight: '89px'
                    }}>
                        <div style={{
                            width: '42px',
                            height: '42px',
                            background: 'var(--primary)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            minWidth: '42px',
                            boxShadow: '0 4px 12px rgba(128, 0, 0, 0.2)',
                            margin: isCollapsed ? '0 auto' : '0'
                        }}>
                            <Icon icon="ion:shield-checkmark" width="22" />
                        </div>
                        {!isCollapsed && (
                            <span style={{
                                fontWeight: 800,
                                fontSize: '18px',
                                color: 'var(--primary)',
                                letterSpacing: '-0.5px'
                            }}>
                                ICFF INDIA
                            </span>
                        )}
                    </div>

                    <nav 
                        className="ro-nav-scrollbar"
                        style={{ 
                            padding: '20px 0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            width: '100%', 
                            gap: '6px', 
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden'
                        }}
                    >
                        {menuGroups.map((group, groupIdx) => (
                            <React.Fragment key={groupIdx}>
                                {!isCollapsed && (
                                    <div className="ro-sidebar-heading">
                                        {group.title}
                                    </div>
                                )}
                                {group.items.map(item => {
                                    const isActive = activeTab === item.id;
                                    return (
                                        <button
                                            key={item.id}
                                            className={`ro-nav-item ${isActive ? 'active' : ''}`}
                                            onClick={() => setActiveTab(item.id)}
                                            style={{
                                                width: '100%',
                                                border: 'none',
                                                background: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                                padding: isCollapsed ? '16px 0' : '14px 28px',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                position: 'relative',
                                                color: isActive ? 'var(--primary)' : 'var(--sidebar-text)',
                                            }}
                                        >
                                            {isActive && (
                                                <motion.div
                                                    layoutId="roActiveTabPill"
                                                    className="ro-nav-active-pill"
                                                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                />
                                            )}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="roActiveTabIndicator"
                                                    className="ro-nav-active-indicator"
                                                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                />
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px', /* Decreased gap from 16px to 12px to look tighter */
                                                position: 'relative',
                                                zIndex: 2,
                                                transform: isActive && !isCollapsed ? 'translateX(6px)' : 'none',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}>
                                                <Icon icon={item.icon} width="24" height="24" style={{
                                                    color: isActive ? 'var(--primary)' : 'var(--sidebar-text)',
                                                    filter: isActive ? 'drop-shadow(0 0 8px rgba(128, 0, 0, 0.3))' : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))',
                                                    transition: 'all 0.3s ease',
                                                    opacity: isActive ? 1 : 0.85
                                                }} />
                                                {!isCollapsed && (
                                                    <span style={{ fontSize: '15px', fontWeight: isActive ? 700 : 500, letterSpacing: '0.1px', opacity: isActive ? 1 : 0.9 }}>
                                                        {item.label}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </nav>


                    <div style={{ marginTop: 'auto', padding: '20px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'center' }}>
                        <button className="universal-logout-btn" onClick={onLogout}>
                            <Icon icon="ion:log-out-outline" width="22" height="22" />
                            {!isCollapsed && <span style={{ fontWeight: 700 }}>Logout</span>}
                        </button>
                    </div>
                </aside>

                <div className="ro-content-area">
                    {/* Isolated Navbar for Run Officer */}
                    <header className="ro-navbar">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '1400px', margin: '0 auto', padding: '0 30px' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
                                    RUN ENGINE <Icon icon="ion:chevron-forward-outline" width="12" style={{ margin: '0 8px' }} />
                                    <span style={{ color: 'var(--primary)' }}>{activeTab?.toUpperCase().replace('-', ' ')}</span>
                                </div>
                            </div>

                            {/* Center: Project Selection */}
                            <div style={{ flex: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>SELECT PROJECT:</div>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <select
                                        value={selectedProject?._id || ''}
                                        onChange={(e) => setSelectedProject(assignedProjects.find(p => p._id === e.target.value))}
                                        style={{
                                            background: 'white',
                                            color: 'var(--primary)',
                                            padding: '10px 48px 10px 20px',
                                            borderRadius: '16px',
                                            fontSize: '13px',
                                            fontWeight: 800,
                                            border: '1px solid var(--border-light)',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            appearance: 'none',
                                            minWidth: '350px',
                                            boxShadow: '0 2px 8px rgba(128, 0, 0, 0.05)',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {assignedProjects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                    </select>
                                    <Icon icon="ion:chevron-down-outline" width="12" style={{ position: 'absolute', right: '16px', pointerEvents: 'none', color: 'var(--primary)' }} />
                                </div>
                            </div>

                            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <div className="ro-profile-pill" onClick={() => setActiveTab('profile')}>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-dark)' }}>{userObj.name || user.name}</div>
                                        <div style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 800, letterSpacing: '1px' }}>{(userObj.description || userObj.designation || user.designation || 'RUN OFFICER').toUpperCase()}</div>
                                    </div>
                                    <div className="ro-profile-avatar">
                                        <Icon icon="ion:person-circle-outline" width="26" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <main className="ro-main-view">
                        {renderContent()}
                    </main>
                </div>

                {/* Floating Remote Control Simulator for Run Officer */}
                <RunOfficerSimulator
                    projectId={selectedProject?._id}
                    onActionSuccess={() => {
                        // Potential global refresh if needed, 
                        // though context and polling handle most things
                    }}
                />
            </div>
        </SimulationProvider>
    );
};


export default RunOfficerDashboard;
